import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Clock, CheckCircle, Building2, Mail, Phone, User, ChevronDown, RefreshCw, CreditCard, Banknote } from 'lucide-react';
import { cn } from '../lib/utils';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AchatsEnAttente = () => {
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [noteAdmin, setNoteAdmin] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => { fetchAchats(); }, []);

  const fetchAchats = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/achats/admin/achats-en-attente`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAchats(data);
      } else {
        toast.error('Erreur lors du chargement des achats');
      }
    } catch {
      toast.error('Erreur lors du chargement des achats');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmer = (achat) => {
    setSelectedAchat(achat);
    setNoteAdmin('');
    setShowConfirmDialog(true);
  };

  const confirmerPaiement = async () => {
    if (!selectedAchat) return;
    setConfirming(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${API_URL}/api/achats/admin/achats/${selectedAchat.id}/confirmer-paiement`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ note_admin: noteAdmin || null })
        }
      );
      if (response.ok) {
        toast.success('Paiement confirme avec succes !');
        setShowConfirmDialog(false);
        fetchAchats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la confirmation');
      }
    } catch {
      toast.error('Erreur lors de la confirmation du paiement');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="achats-en-attente">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Achats en attente de confirmation</h2>
          <p className="text-sm text-gray-500">Validez les virements recus pour assigner les demandes aux COOPER'S</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-600 text-white border-0 text-sm px-3 py-1 font-semibold">{achats.length} en attente</Badge>
          <Button variant="ghost" size="sm" onClick={fetchAchats} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600" data-testid="achats-refresh">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {achats.length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center">
          <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Aucun achat en attente de confirmation</p>
          <p className="text-gray-400 text-sm mt-1">Les achats confirmes apparaitront dans l'historique</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {achats.map((achat) => {
            const isExpanded = expandedIds.has(achat.id);
            const isCB = achat.mode_paiement === 'carte_bancaire_stripe';
            return (
              <div key={achat.id} className={cn("rounded-lg border transition-all duration-150", isExpanded ? "border-amber-300 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm")} data-testid={`achat-row-${achat.id}`}>
                {/* Ligne compacte cliquable */}
                <button
                  onClick={() => toggleExpand(achat.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer select-none"
                  data-testid={`achat-toggle-${achat.id}`}
                >
                  <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", isCB ? "bg-purple-100" : "bg-blue-100")}>
                    {isCB ? <CreditCard className="h-4.5 w-4.5 text-purple-600" /> : <Banknote className="h-4.5 w-4.5 text-blue-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-amber-800" : "text-gray-900")}>
                      {achat.demande?.prenom} {achat.demande?.nom}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {achat.demande?.entreprise || 'Entreprise non renseignee'}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                    <User className="h-3 w-3" />
                    <span className="truncate max-w-[120px]">{achat.cooper?.nom} {achat.cooper?.prenom}</span>
                  </div>
                  <Badge className={cn("shrink-0 text-[10px] font-semibold border", isCB ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-blue-100 text-blue-700 border-blue-200")}>
                    {isCB ? 'Carte' : 'Virement'}
                  </Badge>
                  <span className="text-sm font-bold text-amber-700 tabular-nums shrink-0">{achat.prix_ttc}&euro;</span>
                  <Badge className="bg-amber-600 text-white border border-amber-800 text-[10px] shrink-0 font-semibold">
                    <Clock className="h-2.5 w-2.5 mr-0.5" />
                    En attente
                  </Badge>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-amber-500")} />
                </button>

                {/* Panneau depliant */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`achat-details-${achat.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Col 1: Demandeur */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Demandeur</h4>
                        <div className="space-y-1.5">
                          <p className="text-sm font-medium text-gray-900">{achat.demande?.prenom} {achat.demande?.nom}</p>
                          {achat.demande?.entreprise && (
                            <p className="text-sm text-gray-600 flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-gray-400" /> {achat.demande.entreprise}
                            </p>
                          )}
                          {achat.demande?.email && (
                            <a href={`mailto:${achat.demande.email}`} className="text-sm text-gray-600 hover:text-blue-600 flex items-center gap-1.5 transition-colors">
                              <Mail className="h-3.5 w-3.5 text-gray-400" /> {achat.demande.email}
                            </a>
                          )}
                          {achat.demande?.telephone && (
                            <a href={`tel:${achat.demande.telephone}`} className="text-sm text-gray-600 hover:text-blue-600 flex items-center gap-1.5 transition-colors">
                              <Phone className="h-3.5 w-3.5 text-gray-400" /> {achat.demande.telephone}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Col 2: COOPER + Paiement */}
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Cooper acheteur</h4>
                          <div className="mt-1.5 space-y-1">
                            <p className="text-sm font-medium text-gray-900">{achat.cooper?.nom} {achat.cooper?.prenom}</p>
                            {achat.cooper?.email && <p className="text-xs text-gray-500">{achat.cooper.email}</p>}
                            {achat.cooper?.siret && <p className="text-xs text-gray-400 font-mono">SIRET: {achat.cooper.siret}</p>}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Paiement</h4>
                          <div className="mt-1.5 space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Reference:</span>
                              <span className="font-mono font-semibold text-gray-800">{achat.reference_virement}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Montant:</span>
                              <span className="font-bold text-amber-700">{achat.prix_ttc} &euro; Global</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Date:</span>
                              <span className="text-gray-700">
                                {achat.date_achat ? new Date(achat.date_achat).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Col 3: Action */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Action</h4>
                        <Button
                          onClick={() => handleConfirmer(achat)}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
                          data-testid={`confirm-achat-${achat.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirmer le paiement
                        </Button>
                        <div className="bg-emerald-50 rounded-lg border border-emerald-100 p-2.5">
                          <p className="text-[11px] text-emerald-700 leading-relaxed">
                            La confirmation assigne la demande au COOPER et lui envoie un email avec les coordonnees completes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog de confirmation */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmer le paiement</DialogTitle>
            <DialogDescription>Confirmez que vous avez bien recu le virement bancaire pour cet achat</DialogDescription>
          </DialogHeader>

          {selectedAchat && (
            <div className="space-y-4">
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Demande:</span>
                      <span className="font-medium">{selectedAchat.demande?.prenom} {selectedAchat.demande?.nom}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">COOPER:</span>
                      <span className="font-medium">{selectedAchat.cooper?.nom} {selectedAchat.cooper?.prenom}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reference:</span>
                      <span className="font-mono font-bold">{selectedAchat.reference_virement}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant:</span>
                      <span className="text-lg font-bold text-emerald-600">{selectedAchat.prix_ttc} &euro; Global</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note (optionnelle)</label>
                <Textarea
                  value={noteAdmin}
                  onChange={(e) => setNoteAdmin(e.target.value)}
                  placeholder="Ex: Virement recu le XX/XX/XXXX, numero de transaction..."
                  rows={3}
                />
              </div>

              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <h4 className="font-semibold text-emerald-900 text-sm mb-1.5">Actions effectuees lors de la confirmation :</h4>
                <ul className="list-disc list-inside space-y-0.5 text-xs text-emerald-700">
                  <li>Le statut de l'achat passe a "Paiement confirme"</li>
                  <li>La demande est assignee au COOPER</li>
                  <li>Le COOPER recoit un email de confirmation</li>
                  <li>Le COOPER peut acceder aux coordonnees completes</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={confirming}>Annuler</Button>
            <Button onClick={confirmerPaiement} disabled={confirming} className="bg-emerald-600 hover:bg-emerald-700">
              {confirming ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Confirmation...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" />Confirmer le paiement</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AchatsEnAttente;
